import classNames from 'classnames';
import { h } from 'preact';
import { useLayoutEffect, useRef, useState } from 'preact/hooks';

import { CardIcon, CashIcon } from './Icons';
import { RadioOption } from './Radio';

export interface PaymentButtonsProps {
    options: RadioOption[];
    onSubmit?: (option: RadioOption) => void;
    disabled?: boolean;
    // On the Payment buttons page, which arranges the buttons: each is dragged rather than clicked.
    draggable?: (option: RadioOption) => DraggableAttributes;
}

export interface DraggableAttributes {
    dragged: boolean;
    style?: string;
    onDragStart: (event: DragEvent) => void;
    onDragEnd: () => void;
    onDragOver: (event: DragEvent) => void;
    onDrop: (event: DragEvent) => void;
}

// The labels' line height in pixels (leading-4), used to count their lines.
const LINE_HEIGHT = 16;

/**
 * Which buttons take a full row: neighbours whose names both fit on half a button share a row; any other
 * button, and one left without a partner, takes a full row. The order never changes.
 */
const fullRows = (wide: boolean[]): boolean[] => {
    const full: boolean[] = [];

    for (let i = 0; i < wide.length; i++) {
        if (!wide[i] && i + 1 < wide.length && !wide[i + 1]) {
            full.push(false, false);
            i++;
        } else {
            full.push(true);
        }
    }

    return full;
};

// Card and cash have their colour and icon; any other method is an outlined button with its name only.
const COLOURS: Record<string, string> = { card: 'btn-green', cash: 'btn-muted-orange' };

const Icon = (props: { value: string }): JSX.Element | null => {
    if (props.value !== 'card' && props.value !== 'cash') {
        return null;
    }

    return <span class="inline-flex shrink-0">{props.value === 'card' ? <CardIcon /> : <CashIcon />}</span>;
};

const Label = (props: { option: RadioOption }): JSX.Element => (
    <span class="min-w-0 leading-4 line-clamp-2 [overflow-wrap:anywhere]">{props.option.label}</span>
);

/**
 * The checkout's payment buttons, two to a row and all as tall as one. A name shows on at most two lines and is
 * then cut; it fits half a button when it takes at most two lines there without splitting a word, which a hidden
 * copy of the buttons at half width measures.
 *
 * The Payment buttons page draws these same buttons, draggable, for the cinema to arrange (PaymentButtonsArranger).
 */
export const PaymentButtons = (props: PaymentButtonsProps): JSX.Element => {
    const measureRef = useRef<HTMLDivElement>(null);
    const [wide, setWide] = useState<boolean[]>([]);
    const labels = props.options.map((option) => option.label).join('\n');

    useLayoutEffect(() => {
        const grid = measureRef.current;

        if (!grid) {
            return undefined;
        }

        const measure = (): void => {
            const measured = Array.from(grid.querySelectorAll<HTMLElement>('[data-label]'));

            setWide(
                measured.map(
                    (label) => Math.round(label.offsetHeight / LINE_HEIGHT) > 2 || label.scrollWidth > label.clientWidth
                )
            );
        };

        // Measured again when the checkout column changes width and once the web font has loaded, as either
        // changes how much a name takes.
        let mounted = true;
        const observer = new ResizeObserver(measure);
        observer.observe(grid);
        measure();
        void document.fonts.ready.then(() => mounted && measure());

        return () => {
            mounted = false;
            observer.disconnect();
        };
    }, [labels]);

    const full = fullRows(props.options.map((_, i) => wide[i] ?? false));

    return (
        <div class="relative grow min-w-0">
            <div class="grid grid-cols-2 gap-2">
                {props.options.map((option, i) => {
                    const key = `${option.value}-${option.terminal ?? ''}`;
                    const className = classNames(
                        'btn h-[60px] min-w-0 px-3 text-center',
                        full[i] && 'col-span-2',
                        COLOURS[option.value] ?? 'btn-border bg-white'
                    );
                    const drag = props.draggable?.(option);

                    if (drag) {
                        return (
                            <span
                                key={key}
                                className={classNames(className, 'cursor-grab', drag.dragged && 'opacity-50')}
                                style={drag.style}
                                title={option.label}
                                draggable={true}
                                onDragStart={drag.onDragStart}
                                onDragEnd={drag.onDragEnd}
                                onDragOver={drag.onDragOver}
                                onDrop={drag.onDrop}
                            >
                                <Label option={option} />
                                <Icon value={option.value} />
                            </span>
                        );
                    }

                    return (
                        <button
                            key={key}
                            className={className}
                            title={option.label}
                            onClick={() => props.onSubmit?.(option)}
                            disabled={props.disabled}
                        >
                            <Label option={option} />
                            <Icon value={option.value} />
                        </button>
                    );
                })}
            </div>
            <div ref={measureRef} aria-hidden="true" class="invisible absolute inset-x-0 top-0 grid grid-cols-2 gap-2">
                {props.options.map((option) => (
                    <div key={`${option.value}-${option.terminal ?? ''}`} class="btn min-w-0 px-3">
                        <span data-label class="min-w-0 overflow-hidden leading-4">
                            {option.label}
                        </span>
                        <Icon value={option.value} />
                    </div>
                ))}
            </div>
        </div>
    );
};
